import styled from 'styled-components'
import grey from '@material-ui/core/colors/grey'
import React, {useState} from 'react'
import {RelativeLink} from 'lib/ui/link/RelativeLink'
import {useEventRoutes} from 'organization/Event/EventRoutes'
import {MoreActionsMenu} from 'lib/ui/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import LiveIcon from 'assets/images/ui/icons/live.svg'
import {Tiny, Label, Description} from 'lib/ui/typography'
import defaultLogo from 'assets/images/logo_vertical.png'
import MoveEventDialog from 'organization/EventList/MoveEventDialog'
import {useIsOwner} from 'organization/OwnerProvider'
import {useObvioUser} from 'obvio/auth'
import {ListEventData} from 'organization/EventList'
import HasPermission from 'organization/HasPermission'
import {
  CREATE_EVENTS,
  usePermissions,
  VIEW_STATISTICS,
} from 'organization/PermissionsProvider'
import DeleteEventDialog from 'organization/EventList/DeleteEventDialog'
import ArchiveEventDialog from 'organization/EventList/ArchiveEventDialog'
import UnarchiveEventDialog from 'organization/EventList/UnarchiveEventDialog'
import {getSystemDomain} from 'lib/url'
import ArchiveEventButtons from 'organization/EventList/Cards/ArchiveEventButtons'

export default function GridCard(props: {
  event: ListEventData
  onDelete: (event: {id: number}) => void
  onArchive: (event: {id: number}) => void
  onUnarchive: (event: {id: number}) => void
}) {
  const {event, onDelete, onArchive, onUnarchive} = props
  const label = `view ${event.name}`
  const routes = useEventRoutes(event)
  const {can} = usePermissions()
  const [showMoveDialog, setShowMoveDialog] = useState<boolean>(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false)
  const defaultRoute = can(VIEW_STATISTICS) ? routes.statistics : routes.root

  const systemDomain = getSystemDomain(event)

  return (
    <Container aria-label="event-item">
      <Content>
        <RelativeLink
          to={defaultRoute}
          disableStyles
          disabled={event.is_deleting}
          aria-label={label}
        >
          <Box>
            <EventLogo event={event} />
          </Box>
        </RelativeLink>
        <Bottom>
          <IndicatorsContainer>
            <LiveIndicator isLive={props.event.is_live} />
            <OnlineIndicator isOnline={props.event.is_online} />
          </IndicatorsContainer>
          <MoreActionsMenu>
            <RelativeLink
              to={routes.duplicate}
              disableStyles
              disabled={event.is_deleting}
              aria-label="duplicate event"
            >
              <StyledMenuItem disabled={event.is_deleting}>
                Duplicate
              </StyledMenuItem>
            </RelativeLink>
            <HasPermission required={CREATE_EVENTS}>
              <ArchiveEventButtons
                event={event}
                setShowArchiveDialog={setShowArchiveDialog}
                setShowUnarchiveDialog={setShowUnarchiveDialog}
              />
            </HasPermission>

            <MoveToOrganizationButton
              setShowMoveDialog={setShowMoveDialog}
              event={event}
            />
            <DeleteEventButton
              setShowDeleteDialog={setShowDeleteDialog}
              event={event}
            />
          </MoreActionsMenu>
        </Bottom>
      </Content>
      <Name>{props.event.name}</Name>
      <URL>
        {props.event.slug}.{systemDomain}
      </URL>

      <MoveEventDialog
        event={event}
        isVisible={showMoveDialog}
        onClose={() => {
          setShowMoveDialog(false)
        }}
      />

      <DeleteEventDialog
        event={event}
        visible={showDeleteDialog}
        onDelete={onDelete}
        onClose={() => {
          setShowDeleteDialog(false)
        }}
      />

      <ArchiveEventDialog
        event={event}
        visible={showArchiveDialog}
        onArchived={onArchive}
        onClose={() => {
          setShowArchiveDialog(false)
        }}
      />

      <UnarchiveEventDialog
        event={event}
        visible={showUnarchiveDialog}
        onUnarchived={onUnarchive}
        onClose={() => {
          setShowUnarchiveDialog(false)
        }}
      />
    </Container>
  )
}

function EventLogo(props: {event: ListEventData}) {
  const {event} = props
  const logo = event.logo?.url || defaultLogo

  if (event.is_copying) {
    return <>Building Your Duplicate</>
  }

  if (event.is_deleting) {
    return <>Deleting</>
  }

  return <Image src={logo} alt={event.name} aria-label="event logo" />
}

export function MoveToOrganizationButton(props: {
  setShowMoveDialog: (status: boolean) => void
  event: ListEventData
}) {
  const {setShowMoveDialog, event} = props

  const isOwner = useIsOwner()
  const user = useObvioUser()

  if (!isOwner && !user.is_admin) {
    return null
  }

  return (
    <MenuItem
      aria-label="move to organization"
      disabled={event.is_deleting}
      onClick={() => {
        setShowMoveDialog(true)
      }}
    >
      Move to organization
    </MenuItem>
  )
}

export function DeleteEventButton(props: {
  setShowDeleteDialog: (status: boolean) => void
  event: ListEventData
}) {
  const {setShowDeleteDialog, event} = props

  const isOwner = useIsOwner()
  const user = useObvioUser()

  if (!isOwner && !user.is_admin) {
    return null
  }

  return (
    <MenuItem
      aria-label="delete event"
      disabled={event.is_deleting}
      onClick={() => {
        setShowDeleteDialog(true)
      }}
    >
      Delete event
    </MenuItem>
  )
}

export function LiveIndicator(props: {isLive: boolean}) {
  if (!props.isLive) {
    return (
      <IndicatorContainer>
        <LiveTypography aria-label="expired event">Live</LiveTypography>
        <EmptyCircle aria-label="expired indicator icon" />
      </IndicatorContainer>
    )
  }

  return (
    <IndicatorContainer>
      <LiveTypography isActive aria-label="live event">
        Live
      </LiveTypography>
      <StyledIcon
        src={LiveIcon}
        aria-label="live indicator icon"
        alt="live indicator icon"
      />
    </IndicatorContainer>
  )
}

export function OnlineIndicator(props: {isOnline: boolean}) {
  if (!props.isOnline) {
    return (
      <IndicatorContainer>
        <LiveTypography aria-label="offline event">Online</LiveTypography>
        <EmptyCircle aria-label="offline indicator icon" />
      </IndicatorContainer>
    )
  }

  return (
    <IndicatorContainer>
      <LiveTypography isActive aria-label="online event">
        Online
      </LiveTypography>
      <StyledIcon
        src={LiveIcon}
        aria-label="online indicator icon"
        alt="online indicator icon"
      />
    </IndicatorContainer>
  )
}

const Box = styled.div`
  padding: ${(props) => props.theme.spacing[5]};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 180px;
  color: ${(props) => props.theme.colors.text.primary};

  &:hover {
    background: ${grey[200]};
  }
`

const Name = styled(Label)`
  margin: 0;
  font-weight: 400;
`

const URL = styled(Description)`
  margin-top: ${(props) => props.theme.spacing[1]};
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.spacing[4]};
  border-radius: ${(props) => props.theme.spacing[1]};
  &:hover img {
    opacity: 0.5;
  }
`

const IndicatorContainer = styled.div<{}>`
  margin-left: ${(props) => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  margin-top: ${(props) => props.theme.spacing[2]};
  margin-bottom: ${(props) => props.theme.spacing[2]};

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-left: ${(props) => props.theme.spacing[4]};
    display: flex;
    align-items: center;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.spacing[3]};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
`

const Bottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${(props) => props.theme.colors.gray300};
  padding-left: ${(props) => props.theme.spacing[2]};
`

const IndicatorsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.spacing[1]};
`

const LiveTypography = styled(Tiny)<{
  isActive?: boolean
}>`
  color: ${(props) =>
    props.isActive ? props.theme.colors.primary : grey[500]};
`

const Image = styled.img`
  max-width: 100%;
  max-height: 152px;
`

const StyledMenuItem = styled(MenuItem)`
  color: ${(props) => props.theme.colors.text.primary};
`

const StyledIcon = styled.img`
  height: 12px;
  margin-left: ${(props) => props.theme.spacing[2]};
`

const EmptyCircle = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid ${grey[500]};
  margin-left: ${(props) => props.theme.spacing[2]};
`
